/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { noop } from 'lodash';
import { useContext } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Modal, Text } from '@blend/enterprise-components';

import { DocumentType } from 'blb-constants/DocumentTypes';
import { ModalContent } from 'blb-components/ModalContent';
import { DisablednessContext } from 'blb-contexts/Disabledness';

import { DocUploader } from './DocUploader';
import { BlendBridge } from 'blb-types/blendBridge';

const styles = StyleSheet.create({
  footer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 28,
  },
  headerBar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  spacer: {
    marginBottom: 8,
  },
});

export interface UploadedDocument {
  documentId: string;
  name: string;
}

export namespace DocUploadModal {
  export interface Props {
    afterTask?: { buttonText: string; fetchUrl: string };
    allowMultipleUploads?: boolean;
    allowedFileExtensions?: BlendBridge.AllowedFileExtensions[];
    closingDesignation?: EClose.DocumentClosingDesignation;
    description?: JSX.Element;
    documentTargets?: string[]; // To overwrite the document targets field
    documentType?: string;
    footerText?: string;
    handleClose: () => void;
    isOpen: boolean;
    onSuccess?: (documents: UploadedDocument[]) => Promise<void> | void;
    selectableDocTypes?: DocumentType[];
    sourceType?: EClose.SourceType;
    subTitle?: string;
    taskId?: string;
    title: string;
    uploadLimit?: BlendBridge.API.UploadLimit;
  }
}

export const DocUploadModal: React.FC<DocUploadModal.Props> = ({
  afterTask,
  uploadLimit,
  allowMultipleUploads,
  closingDesignation,
  description,
  documentType,
  handleClose,
  isOpen,
  onSuccess,
  selectableDocTypes,
  sourceType,
  subTitle,
  taskId,
  title,
  footerText,
  documentTargets,
  allowedFileExtensions,
}) => {
  const { disabled } = useContext(DisablednessContext);
  const handleCloseAware = disabled ? noop : handleClose;

  return (
    // TODO: Can refactor this class to use TaskModal.tsx
    <Modal contentLabel={title} handleClose={handleCloseAware} isOpen={isOpen}>
      <ModalContent handleClose={handleCloseAware} headerText={title} subHeaderText={subTitle}>
        {description && <div className={css(styles.spacer)}>{description}</div>}
        <DocUploader
          afterTask={afterTask}
          allowMultipleUploads={allowMultipleUploads}
          allowedFileExtensions={allowedFileExtensions}
          closingDesignation={closingDesignation}
          documentTargets={documentTargets}
          documentType={documentType}
          handleClose={handleClose}
          onSuccess={onSuccess}
          selectableDocTypes={selectableDocTypes}
          sourceType={sourceType}
          taskId={taskId}
          uploadLimit={uploadLimit}
        />
        {footerText && (
          <div className={css(styles.footer)}>
            <Text colorName="NIGHT_80" fontSize="sm">
              {footerText}
            </Text>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
